import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowRightLeft,
  Boxes,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileText,
  Gauge,
  History,
  Images,
  Inbox,
  LayoutGrid,
  MapPin,
  PackageCheck,
  PackageOpen,
  PackagePlus,
  Printer,
  ReceiptRussianRuble,
  ScanLine,
  ScanSearch,
  Scale,
  Settings as SettingsIcon,
  ShieldCheck,
  Truck,
  Users,
} from "lucide-react";
import type React from "react";

import type { SettingsSection } from "@/api/portal";

export interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  permission?: string;
  /** Раздел инсталляции, к которому относится пункт. По умолчанию совпадает с
   *  `permission`; задаётся явно, если они расходятся. */
  page?: string;
  /** Описание для карточки на главной. Есть только у пунктов-модулей —
   *  по нему `DashboardPage` их и отбирает. */
  description?: string;
  /** Пункт-заглушка для сломанной секции (см. `settingsNavItems`): рисуется
   *  красным и не ссылкой. */
  error?: boolean;
}

/** Блок модулей в меню и на главной. */
export interface ModuleGroup {
  title: string;
  items: NavItem[];
}

/** Модули портала по блокам: и меню, и карточки на главной — одна таблица.
 *
 * Блоки и порядок — решение владельца 04.10.2026 по замеру использования за
 * 30 дней: блоки — по процессу, внутри блока — по частоте. Блок — свойство
 * данных (пункт лежит в массиве своего блока), а не разметка потребителя:
 * меню и главная берут его отсюда через `visibleModuleGroups`.
 *
 * Держать вторую таблицу (маршрут + иконка отдельно для главной) значило бы,
 * что переезд модуля правится в двух местах, а ключ — голая строка, и
 * рассинхрон ничем не ловится. */
export const MODULE_GROUPS: ModuleGroup[] = [
  {
    title: "Отгрузка клиентам",
    items: [
      {
        to: "/order-check",
        label: "Проверка заказов",
        icon: ClipboardCheck,
        permission: "order_check",
        description: "Сборка заказов покупателей по паллетам, расходная в 1С.",
      },
      {
        // Отдельный пункт, а не кнопка внутри сборки: раскладка переписывает
        // задание бота и решает, что уедет клиенту. Право `order_check` есть у
        // каждого сборщика, поэтому и право здесь своё.
        to: "/order-plan",
        label: "Раскладка паллет",
        icon: LayoutGrid,
        permission: "order_plan",
        description: "Сверка и правка раскладки заказа по паллетам.",
      },
      {
        to: "/shipment",
        label: "Проверка отгрузки",
        icon: PackageOpen,
        permission: "shipment_check",
        description: "Погрузка паллет по заказам, проведение расходных.",
      },
      {
        // Отдельное право: журнал показывает чужую работу целиком (кто, когда,
        // сколько руками) — он для разбора претензий, а не для каждого сборщика.
        to: "/order-journal",
        label: "Журнал отгрузки",
        icon: History,
        permission: "order_journal",
        description: "Как собран и отгружен заказ: паллеты, фото, сканы, сверка с заказом.",
      },
    ],
  },
  {
    title: "Маркетплейсы",
    items: [
      {
        to: "/fbs",
        label: "ФБС (Wildberries)",
        icon: Boxes,
        permission: "fbs",
        description: "Сбор ФБС: скан, стикеры, завоз в комнату, документы 1С.",
      },
      {
        to: "/ozon",
        label: "ФБС (Ozon)",
        icon: PackageOpen,
        permission: "ozon_fbs",
        description: "Отправления Ozon к сбору.",
      },
    ],
  },
  {
    title: "Приход",
    items: [
      {
        to: "/receiving",
        label: "Приёмка поставок",
        icon: PackagePlus,
        permission: "receiving",
        description:
          "Приход по каждой накладной: партии, этикетки, фото мест, приходная накладная.",
      },
      {
        to: "/receiving-review",
        label: "Журнал приёмки",
        icon: Images,
        permission: "receiving_review",
        description: "Просмотр приходов: фото приёмки и состав заказов.",
      },
      {
        // Отдельное право: ОКК решает судьбу партии (в работу / карантин /
        // возврат), кладовщику приёмки это не выдаётся вместе с приёмкой.
        to: "/okk-receiving",
        label: "ОКК: входной контроль",
        icon: ShieldCheck,
        permission: "okk_receiving",
        description: "Проверка приходов отделом качества: вердикты по позициям, образцы, фото.",
      },
      {
        to: "/returns",
        label: "Приёмка возвратов",
        icon: PackageCheck,
        permission: "returns_intake",
        description: "Сканирование возвратов с маркетплейса.",
      },
      {
        to: "/returns-processing",
        label: "Обработка возвратов",
        icon: FileText,
        permission: "returns_processing",
        description: "Разбор возвратов, служебные записки на утиль.",
      },
    ],
  },
  {
    title: "Склад",
    items: [
      {
        to: "/inventory",
        label: "Инвентаризация",
        icon: ClipboardList,
        permission: "inventory",
        description: "Пересчёт склада сканером, документ инвентаризации в 1С.",
      },
      {
        // Без заказа: юрлицо и склады выбирает кладовщик, «Сохранить» создаёт и
        // проводит «Перемещение запасов». Проверка перемещений — по заказу.
        to: "/stock-transfer",
        label: "Перемещение",
        icon: ArrowRightLeft,
        permission: "stock_transfer",
        description: "Перемещение запасов между складами без заказа: скан и проведение в 1С.",
      },
      {
        to: "/transfer-check",
        label: "Проверка перемещений",
        icon: ArrowLeftRight,
        permission: "transfer_check",
        description: "Проверка перемещений между складами.",
      },
      {
        // Двойное подтверждение в склад из списка и из него (по заказу и
        // «Перемещение» без заказа): получатель принимает отправленное сканом. Только профиль omega (у chekalova раздела нет).
        to: "/transfer-receipts",
        label: "Приёмка перемещений",
        icon: Inbox,
        permission: "transfer_receive",
        description: "Входящие перемещения на ваши склады: скан привезённого, «Принять».",
      },
      {
        // Отдельное право: решение по расхождению пишет (или не пишет) в 1С.
        to: "/transfer-discrepancies",
        label: "Расхождения перемещений",
        icon: Scale,
        permission: "transfer_discrepancy",
        description: "Принятое не совпало с отправленным: пересчитать, провести по принятому, отменить.",
      },
      {
        to: "/boxes",
        label: "Грузоместа",
        icon: PackageOpen,
        permission: "boxes",
        description:
          "Коробки склада и короба/паллеты с упаковки: состав, вскрытие, снятие с паллеты.",
      },
      {
        // Справка по скану: что это, где лежит (юрлицо → склад → партия), расход.
        to: "/label-info",
        label: "Что это?",
        icon: ScanSearch,
        permission: "label_info",
        description: "Скан этикетки: номенклатура, остаток по юрлицам и складам, расход за 90 дней.",
      },
    ],
  },
  {
    title: "Аналитика",
    items: [
      {
        to: "/warehouse-dashboard",
        label: "Дашборд склада",
        icon: Gauge,
        permission: "warehouse_dashboard",
        description:
          "Остатки по юрлицам и складам, сроки годности на МП и ОПТ, оборачиваемость запасов.",
      },
      {
        to: "/warehouse-ops",
        label: "Операции и стоимость",
        icon: Calculator,
        permission: "warehouse_billing",
        description:
          "Сборка, отгрузка и ФБС по дням и направлениям, тарифы с датой и стоимость работы склада.",
      },
    ],
  },
  {
    title: "Логистика",
    items: [
      {
        to: "/logistics",
        label: "Рейсы (логистика)",
        icon: Truck,
        permission: "logistics",
        description: "Рейсы, точки доставки, маршрут и километраж.",
      },
      {
        // Справочник — рабочий экран логистики, а не настройка: адреса заводит
        // сам логист по ходу рейсов.
        to: "/logistics/addresses",
        label: "Справочник адресов",
        icon: MapPin,
        permission: "logistics",
      },
    ],
  },
];

/** Все модули плоским списком, в порядке блоков. */
export const MODULE_ITEMS: NavItem[] = MODULE_GROUPS.flatMap((group) => group.items);

/** Блоки с видимыми пользователю пунктами — в порядке таблицы.
 *
 * Видимость решает потребитель (права, профиль инсталляции — у меню и главной
 * свои правила), здесь только раскладка: блок без единого видимого пункта
 * выкидывается целиком, пустого заголовка не бывает. Принимаем предикат, а не
 * готовый список: пункт вне таблицы потерять так невозможно. */
export function visibleModuleGroups(
  isVisible: (item: NavItem) => boolean,
): ModuleGroup[] {
  return MODULE_GROUPS.map((group) => ({
    title: group.title,
    items: group.items.filter(isVisible),
  })).filter((group) => group.items.length > 0);
}

/** Админские страницы вне настроек разделов (группа «Администрирование»). */
export const ADMIN_ITEMS: NavItem[] = [
  { to: "/admin/users", label: "Пользователи", icon: Users },
  { to: "/admin/activity", label: "Загруженность", icon: Users },
];

/** Справочник баз 1С для SSO-входа — в «Администрировании», а не секцией
 *  настроек: набор секций задаёт реестр бэкенда (`settings_sections.py`) по
 *  профилю инсталляции, а справочник от профиля не зависит — он есть там, где
 *  включён SSO (`/api/config.sso_enabled`). */
export const ONEC_BASES_ADMIN_ITEM: NavItem = {
  to: "/admin/onec-bases",
  label: "Базы 1С (SSO)",
  icon: Database,
};

/** Фронтовый реестр секций настроек: КУДА ведёт ключ секции и какой у него
 *  значок. Набор и порядок секций инсталляции — с сервера
 *  (`/api/config.settings_sections`), здесь только маршрут и иконка.
 *
 *  `label` — ожидаемое имя секции: подсказка при добавлении ключа и то, что
 *  проверяет тест реестра. В меню и на хабе идёт `title` С СЕРВЕРА: два
 *  источника заголовка разъехались бы молча. */
export const SETTINGS_SECTIONS: Record<
  string,
  { to: string; label: string; icon: React.ElementType }
> = {
  general: { to: "/settings/general", label: "Общие", icon: SettingsIcon },
  printing: { to: "/settings/printing", label: "Печать", icon: Printer },
  fbs: { to: "/settings/fbs", label: "ФБС", icon: Boxes },
  order_check: {
    to: "/settings/order-check",
    label: "Проверка заказов",
    icon: ClipboardCheck,
  },
  transfer_check: {
    to: "/settings/transfer-check",
    label: "Перемещения",
    icon: ArrowLeftRight,
  },
  stock_transfer: {
    to: "/settings/stock-transfer",
    label: "Перемещение",
    icon: ArrowRightLeft,
  },
  transfer_receive: {
    to: "/settings/transfer-receipts",
    label: "Приёмка перемещений",
    icon: Inbox,
  },
  receiving: { to: "/settings/receiving", label: "Приёмка", icon: PackagePlus },
  returns: { to: "/settings/returns", label: "Возвраты", icon: PackageCheck },
  inventory: {
    to: "/settings/inventory",
    label: "Инвентаризация",
    icon: ClipboardList,
  },
  logistics: { to: "/settings/logistics", label: "Логистика", icon: Truck },
  warehouse_dashboard: {
    to: "/settings/warehouse-dashboard",
    label: "Дашборд склада",
    icon: Gauge,
  },
  // Тарифы и нормы «Операций и стоимости» — справочник, не отчёт.
  warehouse_billing: {
    to: "/settings/warehouse-billing",
    label: "Тарифы склада",
    icon: ReceiptRussianRuble,
  },
};

/** Пункт-хаб группы «Настройки». */
export const SETTINGS_HUB: NavItem = {
  to: "/settings",
  label: "Все настройки",
  icon: SettingsIcon,
};

/** Личные (не админские) пункты меню аккаунта в шапке.
 *
 * «Проверка сканера» — здесь, а не в `MODULE_GROUPS`: это диагностика своего
 * устройства, у неё нет раздела и права, она есть у каждого в обеих
 * инсталляциях; модуль без права на главной выглядел бы рабочим разделом. */
export const ACCOUNT_ITEMS: NavItem[] = [
  { to: "/account/1c", label: "Подключение 1С", icon: SettingsIcon },
  { to: "/scan-test", label: "Проверка сканера", icon: ScanLine },
];

/** Пункты подменю настроек — строго по списку с сервера.
 *
 * Неизвестный ключ или секция без заголовка — заметная ошибка, а не
 * молчаливый пропуск: секция, добавленная на бэкенде и забытая здесь, иначе
 * просто не появилась бы в меню, и это никто бы не заметил. Раньше это был
 * `throw` — но `settingsNavItems` вызывается прямо во время рендера
 * (`Layout`, `SettingsHubPage`), а ErrorBoundary в портале нет: исключение
 * ронило ВЕСЬ портал белым экраном, а не только пункт сломанной секции.
 * Частый случай — старый чанк в браузере админа после выката (сервер уже
 * отдаёт новую секцию, фронт ещё старый). Поэтому отказ громкий
 * (`console.error`, текст с именем ключа виден в меню), но локальный. */
export function settingsNavItems(sections: SettingsSection[]): NavItem[] {
  return sections.map((section) => {
    const known = SETTINGS_SECTIONS[section.key];
    if (!known) {
      const message =
        `Неизвестный ключ секции настроек с сервера: "${section.key}" — ` +
        "добавьте его в SETTINGS_SECTIONS (src/nav.ts)";
      console.error(message);
      return {
        to: "/settings",
        label: `⚠ Неизвестная секция: ${section.key}`,
        icon: AlertTriangle,
        error: true,
      };
    }
    if (!section.title) {
      const message = `Секция настроек "${section.key}" пришла с сервера без заголовка`;
      console.error(message);
      return {
        to: "/settings",
        label: `⚠ Неизвестная секция: ${section.key}`,
        icon: AlertTriangle,
        error: true,
      };
    }
    return { to: known.to, label: section.title, icon: known.icon };
  });
}
